/**
 * Booking page (owner request, 15 October 2026): a classic booking form, not the chat.
 * Where → what → when → your details → payment (paid types) → confirmed.
 * Runs at /book/<slug>, embedded on a clinic page, and as a view inside the chat window
 * (then it books on the chat's lead, with the details the patient already gave).
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { t, type Locale } from "@wapadesk/i18n";
import { addDays, localDate } from "@wapadesk/engine";
import type {
  BookingPageOptionsResponse,
  BookingSummary,
  HoldBookingResponse,
  SlotsResponse,
} from "@wapadesk/schema";
import { BookApi } from "../lib/book-api";
import { ApiClientError } from "../lib/api";
import type { PaymentState } from "../store";
import { Payment } from "./Payment";
import { BookedBubble } from "./Booking";
import { WaIcon } from "./WaIcon";
import { Tracker } from "../lib/track";
import { timesFor } from "../lib/slots";

type Options = BookingPageOptionsResponse;
type Type = Options["types"][number];
type Where = { kind: "location"; id: string } | { kind: "online" } | { kind: "clinic" };
type Step = "where" | "what" | "when" | "details" | "pay" | "done";

export interface BookingPrefill {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

const DAYS = 7;
const E164 = /^\+[1-9]\d{6,14}$/;

function money(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

/** "07700 900123" → "+447700900123" for a UK clinic; anything with + is kept. */
function normalisePhone(raw: string, timezone: string): string {
  const v = raw.replace(/[\s().-]/g, "");
  if (v.startsWith("+")) return v;
  if (v.startsWith("00")) return `+${v.slice(2)}`;
  if (v.startsWith("0") && timezone === "Europe/London") return `+44${v.slice(1)}`;
  if (v.startsWith("0") && timezone === "Europe/Istanbul") return `+90${v.slice(1)}`;
  return v;
}

const sameWhere = (a: Where | undefined, b: Where) =>
  Boolean(a) &&
  a!.kind === b.kind &&
  (a!.kind !== "location" || a!.id === (b as { id: string }).id);

export function BookingPage({
  clinicRef,
  apiOrigin,
  sessionToken,
  prefill,
  consented,
  onClose,
  onBooked,
  overlayClose,
}: {
  /** In the loader's window over the site: on phones the loader shows its own close button. */
  overlayClose?: boolean | undefined;
  /** Slug (share link) or clinic id (loader, chat). */
  clinicRef: string;
  apiOrigin: string;
  /** Opened from the chat: book on the chat's lead. */
  sessionToken?: string | undefined;
  prefill?: BookingPrefill | undefined;
  /** Consent was already given in the chat. */
  consented?: boolean | undefined;
  onClose?: (() => void) | undefined;
  onBooked?: ((s: BookingSummary) => void) | undefined;
}) {
  const api = useMemo(() => {
    const a = new BookApi(apiOrigin);
    a.token = sessionToken;
    return a;
  }, [apiOrigin, sessionToken]);
  const [opts, setOpts] = useState<Options | undefined>();
  const [failed, setFailed] = useState(false);
  const [step, setStep] = useState<Step>("where");
  const [where, setWhere] = useState<Where | undefined>();
  const [procedures, setProcedures] = useState<string[]>([]);
  const [typeId, setTypeId] = useState<string | undefined>();
  const [from, setFrom] = useState<string | undefined>();
  const [slots, setSlots] = useState<SlotsResponse | undefined>();
  const [day, setDay] = useState<string | undefined>();
  const [slot, setSlot] = useState<{ startAt: string; doctorId: string } | undefined>();
  const [form, setForm] = useState({
    firstName: prefill?.firstName ?? "",
    lastName: prefill?.lastName ?? "",
    email: prefill?.email ?? "",
    phone: prefill?.phone ?? "",
    note: "",
  });
  const [consent, setConsent] = useState(Boolean(consented));
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [payment, setPayment] = useState<PaymentState | undefined>();
  const [summary, setSummary] = useState<BookingSummary | undefined>();
  const [waBusy, setWaBusy] = useState(false);
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [handoffError, setHandoffError] = useState(false);

  // Analytics (D15, 9.4; 16 October 2026): where the page was opened from, the time chosen,
  // payment, confirmation and the WhatsApp tap. Ids and timings only, never details.
  const tracker = useMemo(() => {
    if (!opts || typeof window === "undefined") return undefined;
    const utm: Record<string, string> = {};
    new URLSearchParams(location.search).forEach((v, k) => {
      if (k.startsWith("utm_")) utm[k] = v.slice(0, 200);
    });
    const width = window.innerWidth;
    const from = document.referrer ? pathOf(document.referrer) : undefined;
    return new Tracker(api, {
      clinicId: opts.clinic.id,
      pagePath: from ?? location.pathname,
      device: width < 640 ? "mobile" : width < 1024 ? "tablet" : "desktop",
      locale: opts.clinic.defaultLocale ?? "en-GB",
      ...(Object.keys(utm).length ? { utm } : {}),
    });
  }, [api, opts]);
  const opened = sessionToken
    ? "chat"
    : overlayClose
      ? "overlay"
      : window.parent !== window
        ? "embed"
        : "page";
  useEffect(() => {
    tracker?.track("booking_click", { trigger: `booking_${opened}` });
  }, [tracker, opened]);
  useEffect(() => {
    if (step === "pay") tracker?.track("payment_started", { atNodeId: "booking" });
    if (step === "done" && !onBooked) {
      // Opened from the chat, the chat records the confirmation itself.
      tracker?.track("booking_confirmed", { atNodeId: "booking" });
      toParent({ source: "wapadesk", type: "track", payload: { event: "booking_open" } });
    }
    if (step === "done") tracker?.flush();
  }, [step, tracker, onBooked]);

  const locale = (opts?.clinic.defaultLocale ?? "en-GB") as Locale;
  const tz = opts?.timezone ?? "Europe/London";
  const patientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || tz;

  useEffect(() => {
    let cancelled = false;
    api
      .options(clinicRef)
      .then((o) => {
        if (!cancelled) setOpts(o);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, clinicRef]);

  // ---- what can be chosen where ----
  const choices = useMemo<Where[]>(() => {
    if (!opts) return [];
    const types = opts.types.filter((x) => x.bookable);
    const out: Where[] = [];
    const inPerson = types.filter((x) => x.mode === "in_person");
    if (opts.locations.length)
      for (const l of opts.locations)
        if (inPerson.some((x) => !x.locationIds.length || x.locationIds.includes(l.id)))
          out.push({ kind: "location", id: l.id });
    if (!opts.locations.length && inPerson.length) out.push({ kind: "clinic" });
    if (types.some((x) => x.mode === "online")) out.push({ kind: "online" });
    return out;
  }, [opts]);
  const typesHere = useMemo<Type[]>(() => {
    if (!opts) return [];
    return opts.types.filter((x) => {
      if (!where) return true;
      if (where.kind === "online") return x.mode === "online";
      if (x.mode !== "in_person") return false;
      return where.kind === "clinic" || !x.locationIds.length || x.locationIds.includes(where.id);
    });
  }, [opts, where]);
  const proceduresHere = useMemo(() => {
    if (!opts) return [];
    const loc = where?.kind === "location" ? where.id : undefined;
    return opts.procedures.filter(
      (p) => !loc || !p.locationIds.length || p.locationIds.includes(loc),
    );
  }, [opts, where]);
  const type = typesHere.find((x) => x.id === typeId);
  const locationId = where?.kind === "location" ? where.id : undefined;

  // Skip the "where" step when there is nothing to choose.
  useEffect(() => {
    if (!opts || step !== "where" || where) return;
    if (choices.length === 1) {
      setWhere(choices[0]);
      setStep("what");
    }
  }, [opts, choices, step, where]);
  // One appointment type here: choose it.
  useEffect(() => {
    const bookable = typesHere.filter((x) => x.bookable);
    if (step === "what" && !typeId && bookable.length === 1) setTypeId(bookable[0]!.id);
  }, [step, typesHere, typeId]);

  // ---- free times ----
  useEffect(() => {
    if (step !== "when" || !opts || !type) return;
    const start = from ?? localDate(new Date(), tz).date;
    let cancelled = false;
    setSlots(undefined);
    api
      .slots({
        clinic: opts.clinic.id,
        type: type.id,
        location: locationId,
        from: start,
        days: DAYS,
      })
      .then((r) => {
        if (cancelled) return;
        setSlots(r);
        const firstDay = r.days.find((d) => d.slots.length)?.date;
        setDay((d) => (d && r.days.some((x) => x.date === d && x.slots.length) ? d : firstDay));
      })
      .catch(() => {
        if (!cancelled) setSlots({ timezone: tz, days: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [step, opts, type, locationId, from, api, tz]);

  if (failed)
    return (
      <Shell title="" onClose={onClose}>
        <p class="bp-note">{t("en-GB", "booking.unavailable")}</p>
      </Shell>
    );
  if (!opts)
    return (
      <Shell title="" onClose={onClose}>
        <div class="bp-loading" aria-busy="true">
          <div class="spinner" aria-label="Loading" />
        </div>
      </Shell>
    );
  if (!opts.open || choices.length === 0)
    return (
      <Shell
        title={opts.clinic.name}
        onClose={onClose}
        color={opts.clinic.color}
        overlayClose={overlayClose}
      >
        <p class="bp-note">{t(locale, "bp.closed")}</p>
      </Shell>
    );

  const steps: Step[] = [
    ...(choices.length > 1 ? (["where"] as Step[]) : []),
    "what",
    "when",
    "details",
    ...(type && type.price > 0 ? (["pay"] as Step[]) : []),
  ];
  const stepIndex = steps.indexOf(step);
  const back = () => {
    setError(undefined);
    const i = steps.indexOf(step);
    if (i > 0) setStep(steps[i - 1]!);
  };
  const whereLabel = (w: Where | undefined): string | undefined => {
    if (!w) return undefined;
    if (w.kind === "online") return t(locale, "bp.online");
    if (w.kind === "clinic") return t(locale, "bp.inClinic");
    return opts.locations.find((l) => l.id === w.id)?.name;
  };
  const timeFmt = (iso: string, opt: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...opt, timeZone: patientTz }).format(new Date(iso));

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!type || !slot) return;
    const phone = normalisePhone(form.phone, tz);
    if (!E164.test(phone)) return setError(t(locale, "bp.phoneInvalid"));
    if (!consent) return setError(t(locale, "bp.consentRequired"));
    setBusy(true);
    setError(undefined);
    try {
      const names = opts.procedures.filter((p) => procedures.includes(p.id)).map((p) => p.name);
      const r = await api.book({
        clinicId: opts.clinic.id,
        consultationTypeId: type.id,
        ...(slot.doctorId ? { doctorId: slot.doctorId } : {}),
        ...(locationId ? { locationId } : {}),
        ...(names.length ? { procedures: names } : {}),
        startAt: slot.startAt,
        patientTimezone: patientTz,
        contact: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone,
        },
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
        consent: { data_processing: true, marketing },
        locale,
        ...(typeof location !== "undefined" && document.referrer
          ? { pageUrl: document.referrer }
          : {}),
      });
      if (r.status === "confirmed") {
        setSummary(r.summary);
        onBooked?.(r.summary);
        setStep("done");
      } else {
        setPayment(fromHold(r.hold));
        setStep("pay");
      }
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : "";
      if (code === "slot_unavailable") {
        setSlot(undefined);
        setStep("when");
        setFrom((f) => f ?? localDate(new Date(), tz).date);
        setError(t(locale, "booking.taken"));
      } else if (code === "invalid_body") setError(t(locale, "bp.checkDetails"));
      else setError(t(locale, "booking.unavailable"));
    } finally {
      setBusy(false);
    }
  };

  const awaitPaid = async () => {
    if (!payment) return;
    setPayment({ ...payment, phase: "confirming" });
    for (let i = 0; i < 20; i++) {
      try {
        const st = await api.status(payment.appointmentId);
        if (st.status === "confirmed" && st.summary) {
          setSummary(st.summary);
          onBooked?.(st.summary);
          setPayment(undefined);
          setStep("done");
          return;
        }
        if (st.status === "cancelled") break;
      } catch {
        /* retry */
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    setPayment((p) => (p ? { ...p, phase: "failed" } : p));
  };

  // After booking (owner request, 15 October 2026): send the booking and the details to the team
  // on WhatsApp, or by email, so the conversation starts with everything in it.
  const onWhatsApp = async () => {
    if (waBusy) return;
    setWaBusy(true);
    setHandoffError(false);
    try {
      const h = await api.handoff({ type: "whatsapp", atNodeId: "booking" });
      tracker?.track("whatsapp_click", { atNodeId: "booking", early: false });
      tracker?.flush();
      toParent({ source: "wapadesk", type: "track", payload: { event: "whatsapp_open" } });
      openNewTab(h.url);
    } catch {
      setHandoffError(true);
    } finally {
      setWaBusy(false);
    }
  };
  const onEmail = async () => {
    if (emailState === "sending" || emailState === "sent") return;
    setEmailState("sending");
    try {
      await api.handoff({ type: "email", atNodeId: "booking" });
      setEmailState("sent");
    } catch {
      setEmailState("failed");
    }
  };

  const title =
    step === "done"
      ? t(locale, "booking.confirmed")
      : step === "where"
        ? t(locale, "bp.whereTitle")
        : step === "what"
          ? t(locale, "bp.whatTitle")
          : step === "when"
            ? t(locale, "bp.whenTitle")
            : step === "details"
              ? t(locale, "bp.detailsTitle")
              : t(locale, "booking.payTitle");

  return (
    <Shell
      title={opts.clinic.name}
      onClose={onClose}
      color={opts.clinic.color}
      overlayClose={overlayClose}
    >
      {step !== "done" && (
        <ol class="bp-steps" aria-label={t(locale, "booking.title")}>
          {steps.map((s, i) => (
            <li
              key={s}
              class={i < stepIndex ? "done" : i === stepIndex ? "on" : ""}
              aria-current={i === stepIndex ? "step" : undefined}
            >
              <span>{i + 1}</span> {t(locale, `bp.step.${s}` as "bp.step.what")}
            </li>
          ))}
        </ol>
      )}
      <h2 class="bp-title">{title}</h2>

      {step === "where" && (
        <div class="bp-list" role="list">
          {choices.map((w) => {
            const l = w.kind === "location" ? opts.locations.find((x) => x.id === w.id) : undefined;
            return (
              <button
                key={w.kind === "location" ? w.id : w.kind}
                type="button"
                role="listitem"
                class={`bp-card ${sameWhere(where, w) ? "on" : ""}`}
                onClick={() => {
                  setWhere(w);
                  setTypeId(undefined);
                  setProcedures([]);
                  setSlot(undefined);
                  setStep("what");
                }}
              >
                <strong>{whereLabel(w)}</strong>
                {l?.address && <span class="bp-sub">{l.address}</span>}
                {w.kind === "online" && <span class="bp-sub">{t(locale, "bp.onlineSub")}</span>}
              </button>
            );
          })}
        </div>
      )}

      {step === "what" && (
        <div class="bp-stack">
          {proceduresHere.length > 0 && (
            <fieldset class="bp-field">
              <legend>{t(locale, "bp.treatment")}</legend>
              {[...new Set(proceduresHere.map((p) => p.category))].map((cat) => (
                <div key={cat} class="bp-group">
                  <div class="bp-cat">{cat}</div>
                  <div class="bp-chips">
                    {proceduresHere
                      .filter((p) => p.category === cat)
                      .map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          role="checkbox"
                          aria-checked={procedures.includes(p.id)}
                          class={`bp-chip ${procedures.includes(p.id) ? "on" : ""}`}
                          onClick={() =>
                            setProcedures((s) =>
                              s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id],
                            )
                          }
                        >
                          {p.name}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
              <p class="bp-hint">{t(locale, "bp.treatmentHint")}</p>
            </fieldset>
          )}
          {typesHere.length > 1 && (
            <fieldset class="bp-field">
              <legend>{t(locale, "booking.chooseType")}</legend>
              <div class="bp-list" role="radiogroup">
                {typesHere.map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    role="radio"
                    aria-checked={typeId === x.id}
                    disabled={!x.bookable}
                    class={`bp-card ${typeId === x.id ? "on" : ""}`}
                    onClick={() => {
                      setTypeId(x.id);
                      setSlot(undefined);
                    }}
                  >
                    <span class="bp-row">
                      <strong>{x.name}</strong>
                      <span class="bp-price">
                        {x.price > 0
                          ? money(x.price, x.currency, locale)
                          : t(locale, "booking.free")}
                      </span>
                    </span>
                    <span class="bp-sub">
                      {x.durationMinutes} {t(locale, "booking.min")} ·{" "}
                      {x.mode === "online"
                        ? t(locale, "booking.online")
                        : t(locale, "booking.inPerson")}
                    </span>
                    {x.description && <span class="bp-sub">{x.description}</span>}
                    {!x.bookable && <span class="bp-sub">{t(locale, "booking.paidSoon")}</span>}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          <Nav
            locale={locale}
            onBack={steps.indexOf("what") > 0 ? back : undefined}
            next={{
              label: t(locale, "booking.continue"),
              disabled: !type || !type.bookable,
              onClick: () => setStep("when"),
            }}
          />
        </div>
      )}

      {step === "when" && type && (
        <div class="bp-stack">
          {/* No specialist choice: the clinic assigns the specialist (owner, 15 and 17 October 2026). */}
          <div class="bp-week">
            <button
              type="button"
              class="bp-ghost"
              disabled={!from || from <= localDate(new Date(), tz).date}
              onClick={() => setFrom(addDays(from ?? localDate(new Date(), tz).date, -DAYS))}
            >
              ← {t(locale, "booking.earlier")}
            </button>
            <button
              type="button"
              class="bp-ghost"
              onClick={() => setFrom(addDays(from ?? localDate(new Date(), tz).date, DAYS))}
            >
              {t(locale, "booking.later")} →
            </button>
          </div>
          {!slots && (
            <div class="bp-loading" aria-busy="true">
              <div class="spinner" aria-label="Loading" />
            </div>
          )}
          {slots && (
            <>
              <div class="bp-days" role="tablist">
                {slots.days.map((d) => {
                  const date = new Date(`${d.date}T12:00:00Z`);
                  return (
                    <button
                      key={d.date}
                      type="button"
                      role="tab"
                      aria-selected={day === d.date}
                      disabled={d.slots.length === 0}
                      class={`bp-day ${day === d.date ? "on" : ""}`}
                      onClick={() => setDay(d.date)}
                    >
                      <span>
                        {new Intl.DateTimeFormat(locale, {
                          weekday: "short",
                          timeZone: "UTC",
                        }).format(date)}
                      </span>
                      <strong>
                        {new Intl.DateTimeFormat(locale, {
                          day: "numeric",
                          timeZone: "UTC",
                        }).format(date)}
                      </strong>
                      <span>
                        {new Intl.DateTimeFormat(locale, {
                          month: "short",
                          timeZone: "UTC",
                        }).format(date)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {slots.days.every((d) => d.slots.length === 0) ? (
                <p class="bp-note">{t(locale, "booking.noSlotsWeek")}</p>
              ) : (
                <div class="bp-times">
                  {timesFor(slots.days.find((d) => d.date === day)?.slots ?? []).map((s) => (
                    <button
                      key={`${s.startAt}-${s.doctorId}`}
                      type="button"
                      class={`bp-time ${slot?.startAt === s.startAt ? "on" : ""}`}
                      aria-pressed={slot?.startAt === s.startAt}
                      onClick={() => {
                        setSlot(s);
                        tracker?.track("slot_selected", { atNodeId: "booking" });
                      }}
                    >
                      {timeFmt(s.startAt, { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              )}
              <p class="bp-hint">{t(locale, "booking.timesIn")}</p>
            </>
          )}
          {error && (
            <p class="bp-error" role="alert">
              {error}
            </p>
          )}
          <Nav
            locale={locale}
            onBack={back}
            next={{
              label: t(locale, "booking.continue"),
              disabled: !slot,
              onClick: () => {
                setError(undefined);
                setStep("details");
              },
            }}
          />
        </div>
      )}

      {step === "details" && type && slot && (
        <form class="bp-stack" onSubmit={(e) => void submit(e)}>
          <Summary
            locale={locale}
            lines={[
              type.name,
              whereLabel(where),
              timeFmt(slot.startAt, {
                weekday: "long",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              }),
              type.price > 0 ? money(type.price, type.currency, locale) : t(locale, "booking.free"),
            ]}
          />
          <div class="bp-two">
            <Field
              label={t(locale, "bp.firstName")}
              value={form.firstName}
              autoComplete="given-name"
              onInput={(v) => setForm({ ...form, firstName: v })}
            />
            <Field
              label={t(locale, "bp.lastName")}
              value={form.lastName}
              autoComplete="family-name"
              onInput={(v) => setForm({ ...form, lastName: v })}
            />
          </div>
          <Field
            label={t(locale, "bp.email")}
            type="email"
            value={form.email}
            autoComplete="email"
            onInput={(v) => setForm({ ...form, email: v })}
          />
          <Field
            label={t(locale, "bp.phone")}
            type="tel"
            value={form.phone}
            autoComplete="tel"
            placeholder="+44 7700 900123"
            onInput={(v) => setForm({ ...form, phone: v })}
          />
          <label class="bp-field">
            <span class="bp-label">{t(locale, "bp.note")}</span>
            <textarea
              rows={3}
              maxLength={800}
              value={form.note}
              onInput={(e) => setForm({ ...form, note: (e.target as HTMLTextAreaElement).value })}
            />
          </label>
          {!consented && (
            <label class="bp-check">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent((e.target as HTMLInputElement).checked)}
                required
              />
              <span>
                {t(locale, "bp.consent").replace("{clinic}", opts.clinic.name)}{" "}
                {opts.clinic.privacyUrl && (
                  <a href={opts.clinic.privacyUrl} target="_blank" rel="noopener">
                    {t(locale, "bp.privacy")}
                  </a>
                )}
              </span>
            </label>
          )}
          {!consented && (
            <label class="bp-check">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing((e.target as HTMLInputElement).checked)}
              />
              <span>{t(locale, "bp.marketing")}</span>
            </label>
          )}
          {type.cancellationPolicyText && <p class="bp-hint">{type.cancellationPolicyText}</p>}
          {error && (
            <p class="bp-error" role="alert">
              {error}
            </p>
          )}
          <Nav
            locale={locale}
            onBack={back}
            next={{
              label:
                type.price > 0
                  ? `${t(locale, "bp.toPayment")} (${money(type.price, type.currency, locale)})`
                  : t(locale, "booking.confirm"),
              disabled:
                busy ||
                !form.firstName.trim() ||
                !form.lastName.trim() ||
                !form.email.trim() ||
                !form.phone.trim() ||
                !consent,
              submit: true,
            }}
          />
        </form>
      )}

      {step === "pay" && payment && (
        <div class="bp-stack">
          <Payment
            locale={locale}
            payment={payment}
            onConfirmed={awaitPaid}
            onFailed={() => setPayment((p) => (p ? { ...p, phase: "failed" } : p))}
            onCancel={() => {
              setPayment(undefined);
              setSlot(undefined);
              setStep("when");
            }}
          />
        </div>
      )}

      {step === "done" && summary && (
        <div class="bp-stack">
          <BookedBubble
            locale={locale}
            booked={{
              typeName: summary.type.name,
              doctorName: summary.doctor.name,
              startAt: summary.startAt,
              endAt: summary.endAt,
              timezone: summary.timezone,
              mode: summary.type.mode,
              status: summary.status,
              ...(summary.address ? { address: summary.address } : {}),
              ...(summary.location ? { location: summary.location } : {}),
              ...(summary.meetingUrl ? { meetingUrl: summary.meetingUrl } : {}),
              ...(summary.procedures?.length ? { procedures: summary.procedures } : {}),
              calendar: summary.calendar,
            }}
          />
          <div class="bp-thanks" role="status">
            <svg class="bp-tick" viewBox="0 0 52 52" width="52" height="52" aria-hidden="true">
              <circle cx="26" cy="26" r="24" />
              <path d="M15 27 l7 7 l15 -16" />
            </svg>
            <div>
              <strong>{t(locale, "bp.thanksTitle")}</strong>
              <p>
                {t(locale, "bp.thanksBody").replace(
                  "{name}",
                  form.firstName.trim() ? ` ${form.firstName.trim()}` : "",
                )}
              </p>
            </div>
          </div>
          <p class="bp-hint">{t(locale, "bp.emailed")}</p>
          <p class="bp-hint">{t(locale, "bp.messageTeam")}</p>
          <button
            type="button"
            class="bp-wa"
            disabled={waBusy}
            aria-busy={waBusy}
            onClick={() => void onWhatsApp()}
          >
            <WaIcon /> {t(locale, "chrome.openWhatsApp")}
          </button>
          {opts.clinic.emailHandoff && emailState !== "sent" && (
            <button
              type="button"
              class="bp-outline"
              disabled={emailState === "sending"}
              onClick={() => void onEmail()}
            >
              {emailState === "sending"
                ? t(locale, "chrome.sendingEmail")
                : t(locale, "bp.sendByEmail")}
            </button>
          )}
          {emailState === "sent" && (
            <p class="bp-hint" role="status">
              {t(locale, "bp.emailSent")}
            </p>
          )}
          {(emailState === "failed" || handoffError) && (
            <p class="bp-error" role="alert">
              {t(locale, "bp.handoffFailed")}
            </p>
          )}
          {onClose && (
            <button type="button" class="bp-ghost" onClick={onClose}>
              {t(locale, "bp.done")}
            </button>
          )}
        </div>
      )}
    </Shell>
  );
}

/** "https://site.example/page?x#y" → "/page" (9.4: paths only). */
function pathOf(url: string): string | undefined {
  try {
    return new URL(url).pathname.slice(0, 1024);
  } catch {
    return undefined;
  }
}

/** To the loader that opened this page (overlay or inline embed), never to any other origin. */
function toParent(message: unknown): void {
  if (typeof window === "undefined" || window.parent === window || !document.referrer) return;
  try {
    window.parent.postMessage(message, new URL(document.referrer).origin);
  } catch {
    /* no parent to tell */
  }
}

/** A real anchor click keeps the tap, so phones hand off to the WhatsApp app. */
function openNewTab(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Inside an iframe that blocks the anchor, the loader opens it from the page instead.
  setTimeout(() => {
    if (document.hasFocus())
      toParent({ source: "wapadesk", type: "navigate", payload: { url, target: "_blank" } });
  }, 400);
}

function fromHold(h: HoldBookingResponse): PaymentState {
  return {
    appointmentId: h.appointmentId,
    clientSecret: h.clientSecret,
    stripeAccountId: h.stripeAccountId,
    publishableKey: h.publishableKey,
    amount: h.amount,
    currency: h.currency,
    holdExpiresAt: h.holdExpiresAt,
    phase: "pay",
  };
}

function Shell({
  title,
  color,
  onClose,
  overlayClose,
  children,
}: {
  title: string;
  overlayClose?: boolean | undefined;
  color?: string | undefined;
  onClose?: (() => void) | undefined;
  children: preact.ComponentChildren;
}) {
  return (
    <main class="bp" style={color ? `--bp-accent:${color}` : undefined}>
      <header class="bp-head">
        <span class="bp-name">{title}</span>
        {onClose && (
          <button
            type="button"
            class={`bp-close${overlayClose ? " bp-close-overlay" : ""}`}
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        )}
      </header>
      <div class="bp-body">{children}</div>
      <footer class="bp-foot">
        <a
          href="https://wapadesk.com/?utm_source=widget&utm_medium=badge&utm_campaign=booking_page"
          target="_blank"
          rel="noopener"
        >
          Powered by Wapadesk
        </a>
      </footer>
    </main>
  );
}

function Nav({
  locale,
  onBack,
  next,
}: {
  locale: Locale;
  onBack?: (() => void) | undefined;
  next: { label: string; disabled?: boolean; onClick?: () => void; submit?: boolean };
}) {
  return (
    <div class="bp-nav">
      {onBack ? (
        <button type="button" class="bp-ghost" onClick={onBack}>
          ← {t(locale, "booking.back")}
        </button>
      ) : (
        <span />
      )}
      <button
        type={next.submit ? "submit" : "button"}
        class="bp-primary"
        disabled={next.disabled}
        onClick={next.submit ? undefined : next.onClick}
      >
        {next.label}
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  onInput,
  type = "text",
  autoComplete,
  placeholder,
}: {
  label: string;
  value: string;
  onInput: (v: string) => void;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <label class="bp-field">
      <span class="bp-label">{label}</span>
      <input
        type={type}
        value={value}
        required
        maxLength={type === "email" ? 254 : 60}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      />
    </label>
  );
}

function Summary({ lines }: { locale: Locale; lines: Array<string | undefined> }) {
  return (
    <div class="bp-summary">
      {lines.filter(Boolean).map((l, i) => (
        <div key={i} class={i === 0 ? "bp-summary-main" : ""}>
          {l}
        </div>
      ))}
    </div>
  );
}
